import * as React from 'react';
import { ArrowUpRight, Redo2, Square, Type, Undo2 } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { collectBrowserMeta } from '@/lib/browserMeta';
import {
  ANNOTATION_COLORS,
  DEFAULT_COLOR,
  DEFAULT_TOOL,
  addAnnotation,
  canRedo,
  canUndo,
  createInitialState,
  drawAnnotation,
  drawAnnotations,
  normalizeRect,
  redo,
  undo,
  type Annotation,
  type AnnotationColor,
  type AnnotationTool,
} from './annotationOps';
import { MAX_LONGEST_EDGE, encodeUnderLimit, fitWithin } from './imageExport';
import type { CapturedImage, ScreenshotResult } from './types';

/**
 * Hand-rolled canvas annotation editor (plan §5.4 / D2, D3).
 *
 * No third-party editor dependency (repo minimal-deps culture): a bitmap layer
 * is composited with a replayable op stack of rectangle/arrow/text
 * annotations. Annotations are flattened into the exported image on save (D1 —
 * no vector re-editing in v1). Colors: red (default), yellow, blue. Undo/redo
 * is driven by the pure op stack in `annotationOps.ts`.
 */

export interface AnnotationEditorProps {
  image: CapturedImage;
  /** Called with the flattened image + comment + auto-captured metadata. */
  onSave: (result: ScreenshotResult) => void;
  onCancel: () => void;
}

interface DragState {
  startX: number;
  startY: number;
  curX: number;
  curY: number;
}

interface TextDraft {
  x: number;
  y: number;
  /** CSS-space position for the floating input overlay. */
  cssLeft: number;
  cssTop: number;
  value: string;
}

const COLOR_ORDER: AnnotationColor[] = ['red', 'yellow', 'blue'];

const TOOLS: Array<{ tool: AnnotationTool; label: string; Icon: typeof Square }> = [
  { tool: 'rect', label: 'Rectangle', Icon: Square },
  { tool: 'arrow', label: 'Arrow', Icon: ArrowUpRight },
  { tool: 'text', label: 'Text', Icon: Type },
];

export function AnnotationEditor({ image, onSave, onCancel }: AnnotationEditorProps): JSX.Element {
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const [tool, setTool] = React.useState<AnnotationTool>(DEFAULT_TOOL);
  const [color, setColor] = React.useState<AnnotationColor>(DEFAULT_COLOR);
  const [state, setState] = React.useState(createInitialState);
  const [comment, setComment] = React.useState('');
  const [drag, setDrag] = React.useState<DragState | null>(null);
  const [textDraft, setTextDraft] = React.useState<TextDraft | null>(null);
  const [saving, setSaving] = React.useState(false);

  // Cap the working canvas at MAX_LONGEST_EDGE at editor init — not only at
  // export. A huge pasted photo (e.g. a 48 MP phone screenshot) can exceed a
  // browser's maximum canvas area (notably iOS Safari) and silently produce a
  // blank attachment. redraw() draws the source bitmap scaled into this canvas,
  // so all annotation coordinates live in the capped space.
  const canvasSize = React.useMemo(
    () => fitWithin(image.width, image.height, MAX_LONGEST_EDGE),
    [image.width, image.height],
  );

  // Stroke ~3px on screen, scaled up into the canvas's native pixel space so
  // annotations stay crisp on high-DPR / large captures (plan §5.4).
  const strokeScale = React.useCallback((): number => {
    const canvas = canvasRef.current;
    const rect = canvas?.getBoundingClientRect();
    if (!canvas || !rect || rect.width === 0) return window.devicePixelRatio || 1;
    return canvas.width / rect.width;
  }, []);

  const currentStrokeWidth = () => Math.max(2, Math.round(3 * strokeScale()));
  const currentFontSize = () => Math.max(14, Math.round(18 * strokeScale()));

  /** Repaint bitmap + committed annotations + the in-progress preview. */
  const redraw = React.useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(image.bitmap, 0, 0, canvas.width, canvas.height);
    drawAnnotations(ctx, state.committed);

    if (drag) {
      const preview = buildAnnotation(tool, color, currentStrokeWidth(), drag);
      if (preview) drawAnnotation(ctx, preview);
    }
  }, [image, state, drag, tool, color]);

  React.useEffect(() => {
    redraw();
  }, [redraw]);

  function toCanvasCoords(e: React.PointerEvent): { x: number; y: number } {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
  }

  const onPointerDown = (e: React.PointerEvent) => {
    if (saving) return;
    const { x, y } = toCanvasCoords(e);
    if (tool === 'text') {
      const rect = canvasRef.current!.getBoundingClientRect();
      setTextDraft({
        x,
        y,
        cssLeft: e.clientX - rect.left,
        cssTop: e.clientY - rect.top,
        value: '',
      });
      return;
    }
    (e.target as Element).setPointerCapture?.(e.pointerId);
    setDrag({ startX: x, startY: y, curX: x, curY: y });
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!drag) return;
    const { x, y } = toCanvasCoords(e);
    setDrag({ ...drag, curX: x, curY: y });
  };

  const onPointerUp = () => {
    if (!drag) return;
    const annotation = buildAnnotation(tool, color, currentStrokeWidth(), drag);
    if (annotation) setState((s) => addAnnotation(s, annotation));
    setDrag(null);
  };

  const commitText = () => {
    if (!textDraft) return;
    const value = textDraft.value.trim();
    if (value.length > 0) {
      setState((s) =>
        addAnnotation(s, {
          type: 'text',
          x: textDraft.x,
          y: textDraft.y,
          text: value,
          color,
          fontSize: currentFontSize(),
        }),
      );
    }
    setTextDraft(null);
  };

  const handleSave = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    setSaving(true);
    try {
      // Paint the final flattened image (committed annotations only, no
      // in-progress preview) directly. Relying on setDrag(null) + the memoized
      // redraw() would repaint with a stale closure — redraw still captures the
      // pre-null `drag` this tick and would re-draw the preview stroke.
      const flatCtx = canvas.getContext('2d');
      if (flatCtx) {
        flatCtx.clearRect(0, 0, canvas.width, canvas.height);
        flatCtx.drawImage(image.bitmap, 0, 0, canvas.width, canvas.height);
        drawAnnotations(flatCtx, state.committed);
      }
      setDrag(null);

      const fitted = fitWithin(canvas.width, canvas.height, MAX_LONGEST_EDGE);
      let target = canvas;
      if (fitted.width !== canvas.width || fitted.height !== canvas.height) {
        const scaled = document.createElement('canvas');
        scaled.width = fitted.width;
        scaled.height = fitted.height;
        const sctx = scaled.getContext('2d');
        if (sctx) {
          sctx.drawImage(canvas, 0, 0, fitted.width, fitted.height);
          target = scaled;
        }
      }

      const blob = await encodeUnderLimit(target);
      if (!blob) {
        toast.error(
          'Could not prepare this screenshot for upload — it may be too large even after compression. Try capturing a smaller area.',
        );
        return;
      }
      onSave({ blob, comment: comment.trim(), meta: collectBrowserMeta() });
    } finally {
      setSaving(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    const meta = e.metaKey || e.ctrlKey;
    if (!meta) return;
    const key = e.key.toLowerCase();
    if (key === 'z' && !e.shiftKey) {
      e.preventDefault();
      setState((s) => undo(s));
    } else if ((key === 'z' && e.shiftKey) || key === 'y') {
      e.preventDefault();
      setState((s) => redo(s));
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-black/80 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Annotate screenshot"
      onKeyDown={onKeyDown}
      tabIndex={-1}
    >
      <div className="mx-auto flex w-full max-w-4xl flex-1 flex-col overflow-hidden rounded-lg bg-background shadow-xl">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-3 border-b p-3">
          <div className="flex items-center gap-1" role="group" aria-label="Annotation tools">
            {TOOLS.map(({ tool: t, label, Icon }) => (
              <Button
                key={t}
                type="button"
                variant={tool === t ? 'default' : 'outline'}
                size="icon"
                aria-label={label}
                aria-pressed={tool === t}
                title={label}
                onClick={() => setTool(t)}
              >
                <Icon className="h-4 w-4" />
              </Button>
            ))}
          </div>

          <div className="flex items-center gap-1" role="group" aria-label="Colors">
            {COLOR_ORDER.map((c) => (
              <button
                key={c}
                type="button"
                aria-label={`${c} color`}
                aria-pressed={color === c}
                title={c}
                onClick={() => setColor(c)}
                className={cn(
                  'h-7 w-7 rounded-full border-2 transition-transform',
                  color === c ? 'border-foreground scale-110' : 'border-transparent',
                )}
                style={{ backgroundColor: ANNOTATION_COLORS[c] }}
              />
            ))}
          </div>

          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="outline"
              size="icon"
              aria-label="Undo"
              title="Undo"
              disabled={!canUndo(state)}
              onClick={() => setState((s) => undo(s))}
            >
              <Undo2 className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              aria-label="Redo"
              title="Redo"
              disabled={!canRedo(state)}
              onClick={() => setState((s) => redo(s))}
            >
              <Redo2 className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Canvas stage */}
        <div className="relative flex flex-1 items-center justify-center overflow-auto bg-muted/40 p-4">
          <div className="relative">
            <canvas
              ref={canvasRef}
              width={canvasSize.width}
              height={canvasSize.height}
              className="max-h-[60vh] max-w-full cursor-crosshair touch-none rounded shadow"
              style={{ display: 'block' }}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerLeave={onPointerUp}
              data-testid="annotation-canvas"
            />
            {textDraft && (
              <input
                autoFocus
                aria-label="Annotation text"
                value={textDraft.value}
                onChange={(e) => setTextDraft({ ...textDraft, value: e.target.value })}
                onBlur={commitText}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    commitText();
                  } else if (e.key === 'Escape') {
                    e.preventDefault();
                    setTextDraft(null);
                  }
                }}
                className="absolute rounded border border-input bg-background px-1 text-sm shadow"
                style={{ left: textDraft.cssLeft, top: textDraft.cssTop, minWidth: 120 }}
              />
            )}
          </div>
        </div>

        {/* Comment + actions */}
        <div className="flex flex-col gap-3 border-t p-3">
          <textarea
            aria-label="Screenshot comment"
            placeholder="What should we look at here?"
            value={comment}
            maxLength={2000}
            onChange={(e) => setComment(e.target.value)}
            className="min-h-[60px] w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>
              Cancel
            </Button>
            <Button type="button" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Attach screenshot'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Build the annotation for the active tool from a drag, or `null` when the
 * gesture is too small to be intentional (a click, not a drag).
 */
function buildAnnotation(
  tool: AnnotationTool,
  color: AnnotationColor,
  strokeWidth: number,
  drag: DragState,
): Annotation | null {
  if (tool === 'rect') {
    const r = normalizeRect(drag.startX, drag.startY, drag.curX, drag.curY);
    if (r.w < 3 || r.h < 3) return null;
    return { type: 'rect', ...r, color, strokeWidth };
  }
  if (tool === 'arrow') {
    const dist = Math.hypot(drag.curX - drag.startX, drag.curY - drag.startY);
    if (dist < 6) return null;
    return {
      type: 'arrow',
      x1: drag.startX,
      y1: drag.startY,
      x2: drag.curX,
      y2: drag.curY,
      color,
      strokeWidth,
    };
  }
  return null;
}
